<script setup lang="ts">
import {API_URL, fetchJson, getTimezone, isEmpty} from "../ts/utils";
import {computed, nextTick, ref} from "vue";
import PasswordBasedLogin from "../components/PasswordBasedLogin.vue";
import {$t, ELO_LOCALES, getSelectedLocale, loadMessages, pickLocaleFrom, setLocale} from "../ts/translations";
import LocaleSelector from "../components/LocaleSelector.vue";
import dialog from "../ts/dialog";


async function fetchRef(url :string) {
  const json = await fetchJson(url)
  return ref(json)
}


// EAUTH2-49: language choice comes from server and influenced by "ui_locales" parameter
const signInChoices = await fetchRef(API_URL + '/sign-in-choices');

// EAUTH2-233: allow sign-in page to be embedded in third-party IFrames, but disable plain ELO Credentials
if(signInChoices.value.enablePasswordLogin && window.self.location.origin !== window.top.location.origin) {
  console.log("Disabling ELO Credentials login since inside third-party IFrame")
  signInChoices.value.enablePasswordLogin = false;
}

let currentLocale = getSelectedLocale();
const ui_locales = signInChoices.value.ui_locales ?? ELO_LOCALES;
if(ui_locales &&  ui_locales.length > 0 && !ui_locales.includes(currentLocale)) {
  console.log(`Selecting another locale due to \`ui_locales\` constraint: ${ui_locales}`)
  const newLocale = pickLocaleFrom(ui_locales)
  console.log(`Locale switching: ${currentLocale} => ${newLocale}`)
  await setLocale(newLocale);
}
else {
  await loadMessages();
}


// EAUTH2-197 superseded by EAUTH2-250
// A missing `client_id` is a sign of a missing session.
// This is typically because of a bookmarked page.
// In that case, re-do the /authorize call.
if(!signInChoices.value.client_id && window.location.hash) {
  const base64urlHash = window.location.hash.substring(1)
  const base64hash = base64urlHash.replace(/-/g, '+').replace(/_/g, '/')
  const query = atob(base64hash)
  console.log(`Re-doing /authorize call with query: ${query}`)
  window.location.replace(`${API_URL}/authorize?${query}`)
}


const selectedLocale = ref(getSelectedLocale())

const loading = ref(false)

if (signInChoices.value.provider) {
  console.log(`Signing in using provider "${signInChoices.value.provider}`)
  // EAUTH2-86: SSO with Kerberos/NTLM if selected
  // EAUTH2-280: refactoring / simplification of sign-in flow
  if(signInChoices.value.provider === 'kerberos-ntlm')
    await loginIxSSO()
  else
    signInWith(signInChoices.value.provider)
}

if (signInChoices.value.ixSsoEnabled && !signInChoices.value.enablePasswordLogin && isEmpty(signInChoices.value.providerLabels)) {
  // no other choice than legacy SSO? => trigger it directly
  await loginIxSSO()
}

const errorMessageSSO = ref('')

const hasProviders = computed(() => {
  return !isEmpty(signInChoices.value.providerLabels) || signInChoices.value.ixSsoEnabled
})

// either triggered by the config SSO provider setting, or by a click on the button
async function signInWith(providerId :string) {
  console.log(`Signing in using provider "${providerId}`)
  // EAUTH2-244: do not include /sign-in & /popup-launcher pages in browser history
  // EAUTH2-97 & EAUTH2-249: add forgotten locale with provider authentication
  // EAUTH2-276: show loading spinner while redirecting for a better UX
  loading.value = true
  await nextTick()
  window.location.replace(`${API_URL}/sign-in?provider=${encodeURIComponent(providerId)}&locale=${encodeURIComponent(getSelectedLocale())}&timezone=${encodeURIComponent(getTimezone())}`);
}

async function loginIxSSO() {
  // see EAUTH2-16
  console.log("Signing in using legacy SSO")

  loading.value = true
  const res = await fetch('../../bypsauth/auth?streamversion=9.00.000.000&responseformat=byps-json&language=' + getSelectedLocale())

  // EAUTH2-276: keep loading spinner until final redirection done for a better UX

  if(res.ok) {
    console.log("Signed in with SSO successfully")
    // EAUTH2-192: initialize session options also after legacy ix SSO
    const initSessionRes = await fetch(`${API_URL}/init-session`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json', // Part of EAUTH2-192 but added in EAUTH2-199
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        locale: getSelectedLocale(),
        timezone: getTimezone()
      })
    })
    if(!initSessionRes.ok) {
      console.warn('Failed to initialize session options after SSO')
      console.warn(await res.text())
      // continue login nevertheless with default locale and timezone
    }

    // To resume the redirection flow to the client (possibly code exchange flow)
    // EAUTH2-244: do not include /sign-in & /popup-launcher pages in browser history
    window.location.replace(API_URL + "/check-roles")
  }
  else {
    console.warn('Failed to sign-in with SSO')
    // EAUTH2-276: only remove loading spinner on error
    loading.value = false
    dialog.alert('Failed to sign-in with SSO: ' + await res.text())
  }
}

/**
 * Continue sign-in flow after successful password-based login.
 */
async function onLoginWithPassword() {
  console.log("Continue sign-in flow...")

  // EAUTH2-276: show loading spinner while redirecting for a better UX
  loading.value = true
  await nextTick()

  // To resume the redirection flow to the client (possibly code exchange flow)
  // EAUTH2-244: do not include /sign-in & /popup-launcher pages in browser history
  window.location.replace(API_URL + "/check-roles")
}
</script>



<template>
  <b-loading is-full-page v-model="loading"></b-loading>
  <!-- EAUTH2-276: hide login options while performing SSO -->
  <template v-if="!loading">
    <section v-if="hasProviders">
      <div class="bar-with-text">
        <hr/>
        <p>{{$t('sign_in_with')}}</p>
        <hr/>
      </div>
      <template v-for="pl in signInChoices.providerLabels">
        <b-button expanded
                  type="is-light"
                  class="my-4"
                  @click="signInWith(pl.id)"
                  :disabled="pl.disabled"
                  :data-test-id="'button-sign_in_with_' + pl.id">{{pl.label}}</b-button>
      </template>

      <template v-if="signInChoices.ixSsoEnabled">
        <b-button type="is-light" class="my-4" expanded @click="loginIxSSO()" data-test-id="button-sign_in_legacy_sso">
          {{signInChoices.ixSsoButtonLabel || $t('sign_in_sso') || $t('sign_in')}}
        </b-button>
        <div class="error-message" v-if="errorMessageSSO">
          <b-icon icon="notification-error" class="mx-1"></b-icon>
          <span data-test-id="error-message-sso">{{errorMessageSSO}}</span>
        </div>
      </template>
    </section>

    <template v-if="hasProviders && signInChoices.enablePasswordLogin">
      <section class="bar-with-text">
        <hr/>
        <p>{{$t('or')}}</p>
        <hr/>
      </section>
    </template>

    <section v-if="signInChoices.enablePasswordLogin">
      <PasswordBasedLogin :username="signInChoices.login_hint" @success="onLoginWithPassword()"></PasswordBasedLogin>

      <!-- This is an old WF feature, not widely known, mostly used in the original "Teamspace". Ask @Nesbigall for more details. -->
      <template v-if="signInChoices.hasOldRegistration || signInChoices.hasOldRecovery">
        <p class="old-wf-links">
          <a v-if="signInChoices.legacyWfFeatures.hasRecovery"     :href="'../de.elo.ix.plugin.proxy/wf/apps/app/elo.webapps.Login/?mode=init_recover&lang=' + getSelectedLocale()"  class="has-text-grey">{{$t('recover')}}</a>
          <a v-if="signInChoices.legacyWfFeatures.hasRegistration" :href="'../de.elo.ix.plugin.proxy/wf/apps/app/elo.webapps.Login/?mode=init_register&lang=' + getSelectedLocale()" class="has-text-grey">{{$t('register')}}</a>
        </p>
      </template>
    </section>

    <div class="locale-selector" v-if="ui_locales.length > 1">
      <locale-selector v-model="selectedLocale" :ui_locales="signInChoices.ui_locales"/>
    </div>
  </template>
</template>




<style scoped>
.locale-selector {
  position: absolute;
  top: 24px;
  right: 24px;
}

.bar-with-text {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.bar-with-text > hr {
  flex-grow: 1;
  margin: 0;
  background-color: #CCCCCC;
  height: 1px;
}


.old-wf-links {
  margin-top: 1rem;
  display: flex;
  justify-content: space-between;
}

.old-wf-links > a:only-child {
  margin: auto; /* centered if single link */
}

</style>
